/* Tip split math. Pure functions only — no React, no DOM, no storage.
 *
 * Money is integer cents everywhere. There is no floating-point currency in
 * this file and there must never be: a bar manager reads these numbers off a
 * printout at 1am and staff get paid from them.
 *
 * Fractional inputs are held as integer hundredths so the products stay exact:
 *   hoursCenti   8.25 hours -> 825
 *   pointsCenti  0.50 points -> 50
 *   pctCenti     10.25%      -> 1025
 *
 * A person's weight for an allocation is `hoursCenti * pointsCenti`, an exact
 * integer in units of 1/10,000 hour-points. Ratios of those integers are all
 * the split needs, so the unit itself never has to be named to the user.
 *
 * Allocation uses largest-remainder: floor every raw share, then hand the
 * leftover cents to the biggest fractional remainders, ties broken by roster
 * order. That is what makes the parts always equal the whole.
 */

export const HUNDREDTHS = 100;

export class TipSplitError extends Error {
  constructor(code, details = {}) {
    super(code);
    this.name = "TipSplitError";
    this.code = code;
    this.details = details;
  }
}

/* ── Parsing and formatting ──────────────────────────────────────────────
   Inputs arrive as free text from phone keyboards. Parsers return null for
   anything unparseable so callers can tell "empty" from "typed garbage".
   ───────────────────────────────────────────────────────────────────────── */

// "$1,234.567" -> 123457 cents. Rounds half-up at the third decimal rather
// than silently truncating a number someone actually typed.
export function parseMoneyToCents(input) {
  const s = String(input ?? "").trim().replace(/[$,\s]/g, "");
  if (!s) return 0;
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[2] && !m[3])) return null;
  const whole = m[2] || "0";
  const rawFrac = m[3] || "";
  const cents =
    Number(whole) * 100 + Number(rawFrac.slice(0, 2).padEnd(2, "0") || "0");
  const roundUp = rawFrac.length > 2 && Number(rawFrac[2]) >= 5 ? 1 : 0;
  const total = cents + roundUp;
  return m[1] ? -total : total;
}

// "8.25" -> 825. Used for hours, points, and percents alike.
export function parseHundredths(input) {
  const s = String(input ?? "").trim().replace(/[%\s]/g, "");
  if (!s) return 0;
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[2] && !m[3])) return null;
  const whole = m[2] || "0";
  const rawFrac = m[3] || "";
  const value =
    Number(whole) * 100 + Number(rawFrac.slice(0, 2).padEnd(2, "0") || "0");
  const roundUp = rawFrac.length > 2 && Number(rawFrac[2]) >= 5 ? 1 : 0;
  const total = value + roundUp;
  return m[1] ? -total : total;
}

export function formatCents(cents) {
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100).toLocaleString("en-US");
  const rest = String(abs % 100).padStart(2, "0");
  return `${neg ? "-" : ""}${dollars}.${rest}`;
}

export function formatMoney(cents) {
  const neg = cents < 0;
  return `${neg ? "-" : ""}$${formatCents(Math.abs(cents))}`;
}

export function formatHundredths(value, decimals = 2) {
  const neg = value < 0;
  const abs = Math.abs(value);
  const whole = Math.floor(abs / 100);
  const rest = String(abs % 100).padStart(2, "0");
  const text = decimals === 0 ? String(whole) : `${whole}.${rest.slice(0, decimals)}`;
  return `${neg ? "-" : ""}${text}`;
}

/* ── The allocator ───────────────────────────────────────────────────────── */

/**
 * Split `totalCents` across `weights` so the parts sum to exactly the total.
 *
 * BigInt throughout: the raw share numerator is `total * weight`, which for a
 * large pool and a long roster can pass 2^53 in plain numbers. Exact integer
 * remainders also make the largest-remainder comparison exact, so there is no
 * float tie-breaking anywhere in the payout path.
 *
 * @param {number} totalCents non-negative integer cents
 * @param {number[]} weights non-negative integers, roster order
 * @returns {number[]} integer cents, same order, summing to totalCents
 */
export function distribute(totalCents, weights) {
  if (!Number.isInteger(totalCents) || totalCents < 0) {
    throw new TipSplitError("NEGATIVE_TOTAL", { totalCents });
  }
  const total = BigInt(totalCents);
  const w = weights.map((x) => {
    if (!Number.isInteger(x) || x < 0) {
      throw new TipSplitError("BAD_WEIGHT", { weight: x });
    }
    return BigInt(x);
  });

  const totalWeight = w.reduce((a, b) => a + b, 0n);
  if (totalWeight === 0n) throw new TipSplitError("ZERO_WEIGHT", {});

  const shares = [];
  const remainders = [];
  let allocated = 0n;
  for (let i = 0; i < w.length; i += 1) {
    const numerator = total * w[i];
    const floor = numerator / totalWeight; // BigInt division truncates; both operands are non-negative, so this is floor
    shares.push(floor);
    remainders.push(numerator - floor * totalWeight);
    allocated += floor;
  }

  // Leftover is strictly less than the number of people (every dropped
  // fraction is under one cent), so one pass hands out all of it.
  let leftover = total - allocated;
  const order = shares
    .map((_, i) => i)
    .sort((a, b) => {
      if (remainders[a] !== remainders[b]) return remainders[a] > remainders[b] ? -1 : 1;
      return a - b; // tie: roster order, so the result is reproducible
    });
  for (let k = 0; k < order.length && leftover > 0n; k += 1) {
    shares[order[k]] += 1n;
    leftover -= 1n;
  }

  const out = shares.map(Number);
  const sum = out.reduce((a, b) => a + b, 0);
  if (sum !== totalCents) {
    // Unreachable by construction. Kept as a hard stop so a future edit that
    // breaks the invariant surfaces as an error instead of a wrong payout.
    throw new TipSplitError("SUM_MISMATCH", { expected: totalCents, got: sum });
  }
  return out;
}

// Half-up rounding of `cents * pctCenti / 100`, done in exact integers.
export function percentOfCents(basisCents, pctCenti) {
  if (!Number.isInteger(basisCents) || basisCents < 0) {
    throw new TipSplitError("NEGATIVE_BASIS", { basisCents });
  }
  if (!Number.isInteger(pctCenti) || pctCenti < 0) {
    throw new TipSplitError("BAD_PERCENT", { pctCenti });
  }
  const numerator = BigInt(basisCents) * BigInt(pctCenti) + 5000n;
  return Number(numerator / 10000n);
}

export function weightOf(person) {
  return (person.hoursCenti || 0) * (person.pointsCenti || 0);
}

/* A rule reads exactly one figure off the person it charges. Houses tip the bar
 * off liquor and the runners off food, so those are separate inputs rather than
 * one "sales" number split by guesswork. Nothing here derives one basis from
 * another, so there is no total-vs-parts disagreement to police. */
export const BASES = ["tips", "sales", "food", "liquor"];

export const isBasis = (value) => BASES.indexOf(value) !== -1;

export function basisCentsOf(person, basis) {
  switch (basis) {
    case "sales":
      return person.salesCents || 0;
    case "food":
      return person.foodSalesCents || 0;
    case "liquor":
      return person.liquorSalesCents || 0;
    default:
      return person.tipsCents || 0;
  }
}

/* ── Method A: pool and split ────────────────────────────────────────────── */

/**
 * @param {{poolCents:number, people:Array<{id,name,role,hoursCenti,pointsCenti}>}} input
 */
export function computePoolSplit({ poolCents, people }) {
  const weights = people.map(weightOf);
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) throw new TipSplitError("ZERO_WEIGHT", {});

  const shares = distribute(poolCents, weights);

  return {
    poolCents,
    totalWeightUnits: totalWeight,
    // Hundredths of a weighted hour, for display: 16.50 in the worked example.
    totalWeightedHoursCenti: Math.round(totalWeight / 100),
    // Dollars per weighted hour. Display only — no payout is derived from it.
    ratePerWeightedHour: (poolCents * 100) / totalWeight,
    rows: people.map((p, i) => ({
      id: p.id,
      name: p.name,
      role: p.role,
      hoursCenti: p.hoursCenti || 0,
      pointsCenti: p.pointsCenti || 0,
      weightUnits: weights[i],
      weightedHoursCenti: Math.round(weights[i] / 100),
      shareCents: shares[i],
    })),
  };
}

/* ── Method B: tip-out ───────────────────────────────────────────────────── */

/**
 * What one person's own rules take off one person's own figures.
 *
 * This is the whole of a contributor's arithmetic: each rule reads one figure,
 * rounds once, and the take-home is the tips minus the sum. No deduction is
 * ever charged on another deduction, which is why the money going out always
 * equals the money coming in.
 *
 * The contributor loop below is this function run over a roster. It is split
 * out so one person's ledger can be worked out on its own, and so the loop
 * reads as what it is: the same arithmetic, once per contributor.
 *
 * @param {{person:object, rules:Array, roleName:(id:string)=>string}} input
 * @returns {{tipsCents:number, deductions:Array, paidOutCents:number, takeHomeCents:number, overpaid:boolean}}
 */
export function deductionsFor({ person, rules, roleName = (id) => id }) {
  const tipsCents = person.tipsCents || 0;
  const deductions = rules
    .filter((rule) => rule.fromRoleId === person.roleId)
    .map((rule) => {
      const basisCents = basisCentsOf(person, rule.basis);
      return {
        ruleId: rule.id,
        toRoleId: rule.toRoleId,
        toRoleName: roleName(rule.toRoleId),
        basis: rule.basis,
        basisCents,
        pctCenti: rule.pctCenti,
        amountCents: percentOfCents(basisCents, rule.pctCenti),
      };
    });

  const paidOutCents = deductions.reduce((a, d) => a + d.amountCents, 0);
  const takeHomeCents = tipsCents - paidOutCents;
  return {
    tipsCents,
    deductions,
    paidOutCents,
    takeHomeCents,
    overpaid: takeHomeCents < 0,
  };
}

/**
 * @param {{
 *   people: Array<{id,name,roleId,pointsCenti,hoursCenti,tipsCents,salesCents,foodSalesCents,liquorSalesCents}>,
 *   roles: Array<{id,name}>,
 *   rules: Array<{id,fromRoleId,toRoleId,basis:'tips'|'sales'|'food'|'liquor',pctCenti}>
 * }} input  people are the ones who worked this shift, in roster order
 *
 * Returns `{ valid, problems, ... }`. A setup mistake that makes the whole
 * shift unanswerable still throws, but one person whose rules over-deduct comes
 * back as a problem with every other ledger intact: at 1am the manager needs to
 * see whose line is broken, not an empty panel.
 */
export function computeTipOut({ people, roles, rules }) {
  const roleName = (id) => {
    const r = roles.find((x) => x.id === id);
    return r ? r.name : id;
  };

  // 1 + 2. Every deduction is rounded here, per rule per person, before any
  // pooling. That is what keeps each line on a server's slip a clean number
  // they can check with a phone calculator.
  const contributors = [];
  const problems = [];
  const poolCentsByRole = new Map();

  people.forEach((p) => {
    const tipsCents = p.tipsCents || 0;
    if (!rules.some((r) => r.fromRoleId === p.roleId) && tipsCents === 0) return;

    const { deductions, paidOutCents: paidOut, takeHomeCents } = deductionsFor({
      person: p,
      rules,
      roleName,
    });
    deductions.forEach((d) => {
      poolCentsByRole.set(
        d.toRoleId,
        (poolCentsByRole.get(d.toRoleId) || 0) + d.amountCents
      );
    });

    if (takeHomeCents < 0) {
      // 3. Never print a negative take-home. Name the person and the rules so
      // the manager can fix the setup instead of guessing. The ledger still
      // comes back so they can see which deduction is the problem.
      problems.push({
        code: "NEGATIVE_TAKE_HOME",
        id: p.id,
        name: p.name,
        tipsCents,
        paidOut,
        rules: deductions.map(
          (d) => `${formatHundredths(d.pctCenti)}% of ${d.basis} to ${d.toRoleName}`
        ),
      });
    }

    contributors.push({
      id: p.id,
      name: p.name,
      roleId: p.roleId,
      roleName: roleName(p.roleId),
      tipsCents,
      salesCents: p.salesCents || 0,
      foodSalesCents: p.foodSalesCents || 0,
      liquorSalesCents: p.liquorSalesCents || 0,
      deductions,
      paidOutCents: paidOut,
      takeHomeCents,
      overpaid: takeHomeCents < 0,
    });
  });

  // An unpayable ledger poisons every pool it feeds, so stop before dividing
  // money that is not there. Nothing is printable in this state.
  if (problems.length) {
    return {
      contributors,
      pools: [],
      problems,
      valid: false,
      totalTipsCents: people.reduce((a, p) => a + (p.tipsCents || 0), 0),
      totalTakeHomeCents: 0,
      totalSharesCents: 0,
    };
  }

  // 4. Each receiving role's pool is split by the same Method A allocator.
  const pools = [];
  Array.from(poolCentsByRole.entries()).forEach(([roleId, poolCents]) => {
    const recipients = people.filter((p) => p.roleId === roleId);
    // A rule that has not earned anything yet is not a problem to solve. Only
    // real money pointed at nobody is worth stopping the shift over, which is
    // what keeps a freshly added rule from erroring before anything is typed.
    if (poolCents === 0) return;
    if (recipients.length === 0) {
      // 6. Money pointed at nobody is a setup error, not money that vanishes.
      throw new TipSplitError("NO_RECIPIENTS", {
        roleName: roleName(roleId),
        poolCents,
        amount: formatMoney(poolCents),
      });
    }
    // Being checked off as working is enough to earn a share of a support pool.
    // Hours are an optional refinement here, unlike Method A where the pool is
    // bought with hours: if nobody in the role has any, the pool splits by
    // points alone, which is an even split wherever the points are equal.
    // Making hours mandatory meant a manager had to reconstruct a busser's
    // clock-in before the bar could be paid out, which is not the question
    // tip-out is asking.
    const byHours = recipients.some((p) => (p.hoursCenti || 0) > 0);
    const weights = recipients.map((p) => (byHours ? weightOf(p) : p.pointsCenti || 0));
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    if (totalWeight === 0) {
      // Only reachable now with everyone in the role on zero points, so there
      // is genuinely no ratio to divide by.
      throw new TipSplitError("ROLE_ZERO_WEIGHT", {
        roleName: roleName(roleId),
        poolCents,
        amount: formatMoney(poolCents),
      });
    }
    const shares = distribute(poolCents, weights);
    pools.push({
      roleId,
      roleName: roleName(roleId),
      poolCents,
      totalWeightUnits: totalWeight,
      totalWeightedHoursCenti: Math.round(totalWeight / 100),
      recipients: recipients.map((p, i) => ({
        id: p.id,
        name: p.name,
        hoursCenti: p.hoursCenti || 0,
        pointsCenti: p.pointsCenti || 0,
        weightUnits: weights[i],
        weightedHoursCenti: Math.round(weights[i] / 100),
        shareCents: shares[i],
      })),
    });
  });

  const totalTipsCents = people.reduce((a, p) => a + (p.tipsCents || 0), 0);
  const totalTakeHomeCents = contributors.reduce((a, c) => a + c.takeHomeCents, 0);
  const totalSharesCents = pools.reduce((a, p) => a + p.poolCents, 0);

  // 5. Money in equals money out, to the cent, every time.
  if (totalTipsCents !== totalTakeHomeCents + totalSharesCents) {
    throw new TipSplitError("CONSERVATION_MISMATCH", {
      totalTipsCents,
      totalTakeHomeCents,
      totalSharesCents,
    });
  }

  return {
    contributors,
    pools,
    problems,
    valid: true,
    totalTipsCents,
    totalTakeHomeCents,
    totalSharesCents,
  };
}
